import React from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import {useLocation, useNavigate} from 'react-router';
import {useAccount} from '@igp/shared/auth';
import {LocalizedText} from '@igp/shared/components/i18n';
import {CustomIcon, Icon} from '@igp/shared/components/icons';
import type {MenuProps} from '@igp/shared/components/menu';
import {Locale, Logout, Menu, MenuGroupTitle, MenuTreeItem, UserInfo} from '@igp/shared/components/menu';
import {useUserProfileDetails} from '@igp/shared/features/user-profile';
import {useLocale} from '@igp/shared/localization';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';
import type {Locale as Locales} from '@igp/shared/style';
import {Divider, Stack, Typography} from '@mui/material';
import {SimpleTreeView} from '@mui/x-tree-view';
import {makeStyles} from 'tss-react/mui';

import {ProtectedMenuTreeItem} from '@access-control';
import {routes} from '@config';
import {useAgentId} from '@features/account/hooks';
import {debtTransferLogsPageResource} from '@features/debt-transfer-log';
import {keycloakLogsPageResource} from '@features/keycloak-log';
import {repaymentLogsPageResource} from '@features/repayment-log';
import {
    boUsersPageResource,
    countryLevelPartnerReportResource,
    debtResponsibleMasterAgentsResource,
    negativeCarryOverReportPageResource,
    referralLinkConfigPageResource,
    useDefaultPlayerLevelReportRoute,
} from '@pages';

import ReferralLinkButton from './ReferralLinkButton';

import type {Routes} from '@config';

const localized = defineMessages({
    teamName: {
        id: 'AgentBoMenu_teamName',
        defaultMessage: 'Agent Back Office',
    },
    account: {
        id: 'AgentBoMenu_account',
        defaultMessage: 'ACCOUNT',
    },
    localeLabel: {
        id: 'AgentBoMenu_localeLabel',
        defaultMessage: 'Language',
    },
    logoutLabel: {
        id: 'AgentBoMenu_logoutLabel',
        defaultMessage: 'Logout',
    },

    enLocale: {
        id: 'enLocale',
        defaultMessage: 'EN',
    },
    chLocale: {
        id: 'chLocale',
        defaultMessage: 'CH',
    },
    referralLinkTitle: {
        id: 'ReferralLink_referralLinkTitle',
        defaultMessage: 'Referral link',
    },

    home: {
        id: 'AgentBoMenu_home',
        defaultMessage: 'Home',
    },
    transactionHistory: {
        id: 'AgentBoMenu_transactionHistory',
        defaultMessage: 'Transaction History',
    },
    players: {
        id: 'AgentBoMenu_players',
        defaultMessage: 'Players',
    },
    agents: {
        id: 'AgentBoMenu_agents',
        defaultMessage: 'Agents',
    },
    revenueShareHistory: {
        id: 'AgentBoMenu_revenueShareHistory',
        defaultMessage: 'Revenue Share History',
    },
    report: {
        id: 'AgentBoMenu_report',
        defaultMessage: 'Reports',
    },
    agentLevelReportWeekly: {
        id: 'AgentBoMenu_agentLevelReportWeekly',
        defaultMessage: 'Agent Level Report Weekly',
    },
    playerLevelReport: {
        id: 'AgentBoMenu_playerLevelReport',
        defaultMessage: 'Player Level Report',
    },
    payoutReport: {
        id: 'AgentBoMenu_payoutReport',
        defaultMessage: 'Payout Report',
    },
    negativeCarryoverReport: {
        id: 'AgentBoMenu_negativeCarryoverReport',
        defaultMessage: 'Negative Carryover Report',
    },
    agentLevelReportDaily: {
        id: 'AgentBoMenu_agentLevelReportDaily',
        defaultMessage: 'Agent Level Report Daily',
    },
    countryLevelPartnerReport: {
        id: 'AgentBoMenu_countryLevelPartnerReport',
        defaultMessage: 'Country Level Partner Report',
    },
    debtManagement: {
        id: 'AgentBoMenu_debtManagement',
        defaultMessage: 'Debt Management',
    },
    debtTransferLogs: {
        id: 'AgentBoMenu_debtTransferLogs',
        defaultMessage: 'Debt Transfer Logs',
    },
    debtResponsibleMasterAgents: {
        id: 'AgentBoMenu_debtResponsibleMasterAgents',
        defaultMessage: 'Debt Responsible Master Agents',
    },
    repaymentLogs: {
        id: 'AgentBoMenu_repaymentLogs',
        defaultMessage: 'Repayment Log',
    },
    userManagement: {
        id: 'AgentBoMenu_userManagement',
        defaultMessage: 'User Management ',
    },
    playerEcologyWeekly: {
        id: 'AgentBoMenu_playerEcologyWeekly',
        defaultMessage: 'Player Ecology Weekly Report',
    },
    adminTools: {
        id: 'AgentBoMenu_adminTools',
        defaultMessage: 'Admin Tools',
    },
    referralLinkConfig: {
        id: 'AgentBoMenu_referralLinkConfig',
        defaultMessage: 'Referral Link Configuration',
    },
    keycloakLogs: {
        id: 'AgentBoMenu_keycloakLogs',
        defaultMessage: 'Event Logs',
    },
});

const localizedLocales: Record<Locales, MessageDescriptor> = {
    en: localized.enLocale,
    'zh-CN': localized.chLocale,
};

const useStyles = makeStyles()(theme => ({
    treeView: {
        overflow: 'auto',
    },
    referralLinkContainer: {
        alignItems: 'center',
        paddingLeft: theme.spacing(2.5),
        paddingBottom: theme.spacing(1),
    },
    linkIcon: {
        fontSize: '18px',
    },
}));

type AgentBoMenuProps = Omit<MenuProps, 'name' | 'footer'>;

type ExpandableMenuItems = 'home' | 'report' | 'debtManagement' | 'adminTools';

type PageMenuItems =
    | 'homeTransactionHistory'
    | 'homeRevenueShare'
    | 'players'
    | 'agents'
    | 'agentLevelReportWeekly'
    | 'agentLevelReportDaily'
    | 'playerLevelReport'
    | 'payoutReport'
    | 'negativeCarryoverReport'
    | 'countryLevelPartnerReport'
    | 'debtTransferLogs'
    | 'debtResponsibleMasterAgents'
    | 'repaymentLogs'
    | 'userManagement'
    | 'playerEcologyWeekly'
    | 'referralLinkConfig'
    | 'keycloakLogs';

export function AgentBoMenu({isStick, onIsStickChange}: AgentBoMenuProps) {
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const [hovered, setHovered] = React.useState(false);
    const [selected, setSelected] = React.useState<PageMenuItems>(() => getMenuItemByPathItem());
    const [expanded, setExpanded] = React.useState<string[]>(() => {
        const expanded = getExpandedItemByPath();
        return expanded ? [expanded] : [];
    });
    React.useEffect(() => {
        const currentExpandedItems = getExpandedItemByPath();
        if (!expanded.includes(currentExpandedItems)) {
            setExpanded(prevState => [...prevState, currentExpandedItems]);
        }
        const currentItem = getMenuItemByPathItem();
        if (currentItem !== selected) {
            setSelected(currentItem);
        }
    }, [pathname]);
    const {locale, handleLocaleChange} = useLocale();
    const {handleLogout, preferred_username, email} = useAccount();
    const classes = useStyles().classes;
    const allLocales: Locales[] = ['en', 'zh-CN'];

    const isStickOrHovered = isStick || hovered;

    const agentId = useAgentId();
    const {item} = useUserProfileDetails({
        id: agentId,
        fields: ['uid', 'username'],
        viewType: 'MenuUserInfo',
        cleanDelay: extendedViewCleanDelay,
        isCancelable: false,
    });
    const defaultPlayerLevelReportPage = useDefaultPlayerLevelReportRoute();

    function getExpandedItemByPath() {
        const mapper: Partial<Record<Routes, ExpandableMenuItems>> = {
            [routes.transactionHistory]: 'home',
            [routes.revenueShareHistory]: 'home',
            [routes.agentLevelReportWeekly]: 'report',
            [routes.playerLevelReportShare]: 'report',
            [routes.playerLevelReportPoker]: 'report',
            [routes.playerLevelReportPokerNhleSd]: 'report',
            [routes.playerLevelReportPokerMtt]: 'report',
            [routes.playerLevelReportPokerPlo]: 'report',
            [routes.playerLevelReportCasino]: 'report',
            [routes.playerLevelReportGlobalSpins]: 'report',
            [routes.playerLevelReportSports]: 'report',
            [routes.playerLevelReportBasic]: 'report',
            [routes.playerLevelReportPlayerActivity]: 'report',
            [routes.playerLevelReportPlayerEcology]: 'report',
            [routes.payoutReport]: 'report',
            [routes.negativeCarryoverReport]: 'report',
            [routes.agentLevelReportDaily]: 'report',
            [routes.countryLevelPartnerReportGameRevenue]: 'report',
            [routes.countryLevelPartnerReportUserActivity]: 'report',
            [routes.countryLevelPartnerReportDepositActivity]: 'report',
            [routes.debtTransferLogs]: 'debtManagement',
            [routes.debtResponsibleMasterAgents]: 'debtManagement',
            [routes.repaymentLogs]: 'debtManagement',
            [routes.playerEcologyWeekly]: 'report',
            [routes.userManagement]: 'adminTools',
            [routes.referralLinkConfig]: 'adminTools',
            [routes.keycloakLogs]: 'adminTools',
        };
        return mapper[location.pathname as Routes];
    }

    function getMenuItemByPathItem() {
        const exactRouteMapper: Partial<Record<Routes, PageMenuItems>> = {
            [routes.home]: 'homeTransactionHistory',
            [routes.transactionHistory]: 'homeTransactionHistory',
            [routes.revenueShareHistory]: 'homeRevenueShare',
            [routes.agents]: 'agents',
            [routes.agentDetails]: null,
            [routes.players]: 'players',
            [routes.playerDetails]: null,
            [routes.agentLevelReportWeekly]: 'agentLevelReportWeekly',
            [routes.payoutReport]: 'payoutReport',
            [routes.negativeCarryoverReport]: 'negativeCarryoverReport',
            [routes.agentLevelReportDaily]: 'agentLevelReportDaily',
            [routes.countryLevelPartnerReportGameRevenue]: 'countryLevelPartnerReport',
            [routes.countryLevelPartnerReportUserActivity]: 'countryLevelPartnerReport',
            [routes.countryLevelPartnerReportDepositActivity]: 'countryLevelPartnerReport',
            [routes.debtResponsibleMasterAgents]: 'debtResponsibleMasterAgents',
            [routes.debtTransferLogs]: 'debtTransferLogs',
            [routes.repaymentLogs]: 'repaymentLogs',
            [routes.userManagement]: 'userManagement',
            [routes.playerEcologyWeekly]: 'playerEcologyWeekly',
            [routes.referralLinkConfig]: 'referralLinkConfig',
            [routes.keycloakLogs]: 'keycloakLogs',
        };
        const prefixRouteMapper: Partial<Record<Routes, PageMenuItems>> = {
            [routes.playerLevelReport]: 'playerLevelReport',
        };

        let result: PageMenuItems;
        Object.keys(prefixRouteMapper).forEach((key: Routes) => {
            if (pathname.startsWith(key)) {
                result = prefixRouteMapper[key];
            }
        });
        if (pathname in exactRouteMapper) {
            result = exactRouteMapper[pathname as Routes];
        }

        return result;
    }

    function handleSelect(itemId: PageMenuItems) {
        const mapper: Record<PageMenuItems, Routes> = {
            agentLevelReportWeekly: routes.agentLevelReportWeekly,
            agents: routes.agents,
            homeRevenueShare: routes.revenueShareHistory,
            homeTransactionHistory: routes.transactionHistory,
            playerLevelReport: defaultPlayerLevelReportPage,
            players: routes.players,
            payoutReport: routes.payoutReport,
            negativeCarryoverReport: routes.negativeCarryoverReport,
            agentLevelReportDaily: routes.agentLevelReportDaily,
            countryLevelPartnerReport: routes.countryLevelPartnerReportGameRevenue,
            debtTransferLogs: routes.debtTransferLogs,
            debtResponsibleMasterAgents: routes.debtResponsibleMasterAgents,
            repaymentLogs: routes.repaymentLogs,
            userManagement: routes.userManagement,
            playerEcologyWeekly: routes.playerEcologyWeekly,
            referralLinkConfig: routes.referralLinkConfig,
            keycloakLogs: routes.keycloakLogs,
        };

        if (itemId !== selected) {
            setSelected(itemId);
            navigate(mapper[itemId]);
        }
    }
    return (
        <Menu
            name={localized.teamName}
            isStick={isStick}
            onIsStickChange={onIsStickChange}
            onHoverChanged={setHovered}
            footer={
                <>
                    {isStickOrHovered && <MenuGroupTitle title={localized.account} />}
                    <Locale
                        label={localized.localeLabel}
                        locales={allLocales.map(l => ({value: l, label: localizedLocales[l]}))}
                        value={locale}
                        onChange={handleLocaleChange}
                        minimizeMode={!isStickOrHovered}
                    />
                    <Logout label={localized.logoutLabel} onLogout={handleLogout} minimizeMode={!isStickOrHovered} />
                    <Stack direction="row" spacing={2} className={classes.referralLinkContainer}>
                        <Icon icon={CustomIcon.Link} className={classes.linkIcon} />
                        <Typography variant="body2">
                            <LocalizedText label={localized.referralLinkTitle} />
                        </Typography>
                        <ReferralLinkButton uid={agentId} />
                    </Stack>
                    <Divider />
                    <UserInfo
                        agentId={agentId}
                        username={item?.username ?? preferred_username}
                        email={email}
                        avatarOnly={!isStickOrHovered}
                    />
                </>
            }
        >
            <SimpleTreeView
                expandedItems={expanded}
                onExpandedItemsChange={(_, nodeIds: string[]) => setExpanded(nodeIds)}
                selectedItems={selected}
                onSelectedItemsChange={(_, nodeId) => handleSelect(nodeId as PageMenuItems)}
                multiSelect={false}
                className={classes.treeView}
            >
                <MenuTreeItem<ExpandableMenuItems> id="home" title={localized.home} menuIcon="ServicesOutline">
                    <MenuTreeItem<PageMenuItems> id="homeTransactionHistory" title={localized.transactionHistory} hideMenuIcon />
                    <MenuTreeItem<PageMenuItems> id="homeRevenueShare" title={localized.revenueShareHistory} hideMenuIcon />
                </MenuTreeItem>
                <MenuTreeItem<PageMenuItems> id="players" title={localized.players} menuIcon="UserOutline" />
                <MenuTreeItem<PageMenuItems> id="agents" title={localized.agents} menuIcon="Profile" />
                <MenuTreeItem<ExpandableMenuItems> id="report" title={localized.report} menuIcon="ArticleOutline">
                    <MenuTreeItem<PageMenuItems> id="agentLevelReportWeekly" title={localized.agentLevelReportWeekly} hideMenuIcon />
                    <MenuTreeItem<PageMenuItems> id="agentLevelReportDaily" title={localized.agentLevelReportDaily} hideMenuIcon />
                    <MenuTreeItem<PageMenuItems> id="playerLevelReport" title={localized.playerLevelReport} hideMenuIcon />
                    <MenuTreeItem<PageMenuItems> id="payoutReport" title={localized.payoutReport} hideMenuIcon />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="negativeCarryoverReport"
                        title={localized.negativeCarryoverReport}
                        hideMenuIcon
                        resources={[negativeCarryOverReportPageResource]}
                    />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="countryLevelPartnerReport"
                        title={localized.countryLevelPartnerReport}
                        hideMenuIcon
                        resources={[countryLevelPartnerReportResource]}
                    />
                    <MenuTreeItem<PageMenuItems> id="playerEcologyWeekly" title={localized.playerEcologyWeekly} hideMenuIcon />
                </MenuTreeItem>
                <ProtectedMenuTreeItem<ExpandableMenuItems>
                    id="debtManagement"
                    title={localized.debtManagement}
                    menuIcon="Withdrawal"
                    resources={[debtResponsibleMasterAgentsResource, debtTransferLogsPageResource, repaymentLogsPageResource]}
                >
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="debtResponsibleMasterAgents"
                        title={localized.debtResponsibleMasterAgents}
                        hideMenuIcon
                        resources={[debtResponsibleMasterAgentsResource]}
                    />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="debtTransferLogs"
                        title={localized.debtTransferLogs}
                        hideMenuIcon
                        resources={[debtTransferLogsPageResource]}
                    />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="repaymentLogs"
                        title={localized.repaymentLogs}
                        hideMenuIcon
                        resources={[repaymentLogsPageResource]}
                    />
                </ProtectedMenuTreeItem>
                <ProtectedMenuTreeItem<ExpandableMenuItems>
                    id="adminTools"
                    title={localized.adminTools}
                    menuIcon="Access"
                    resources={[boUsersPageResource, referralLinkConfigPageResource, keycloakLogsPageResource]}
                >
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="userManagement"
                        title={localized.userManagement}
                        hideMenuIcon
                        resources={[boUsersPageResource]}
                    />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="referralLinkConfig"
                        title={localized.referralLinkConfig}
                        hideMenuIcon
                        resources={[referralLinkConfigPageResource]}
                    />
                    <ProtectedMenuTreeItem<PageMenuItems>
                        id="keycloakLogs"
                        title={localized.keycloakLogs}
                        hideMenuIcon
                        resources={[keycloakLogsPageResource]}
                    />
                </ProtectedMenuTreeItem>
            </SimpleTreeView>
        </Menu>
    );
}
